import React, { useState, useEffect, useCallback } from 'react';
import { Trophy, Undo2, AlertCircle, Check, ArrowLeft, RotateCcw, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTheme } from '../../contexts/ThemeContext';
import ConfirmModal from '../../components/ConfirmModal';
import { volleyballGameAPI } from './services/volleyballGameApi';

/**
 * VolleyballScoring Component
 *
 * Set-based scoring for volleyball. Each set goes to maxPoints (default 25),
 * the final set to finalSetPoints (default 15). Teams must win by 2 (winBy2).
 * Match winner is the first team to win ceil(bestOf/2) sets.
 *
 * Props (from GameComponent modular flow):
 * - gameCommonId: existing game record ID
 * - gameSettings: { bestOf, maxPoints, finalSetPoints, isRally, winBy2 }
 * - participantAInfo: { name, type, id }
 * - participantBInfo: { name, type, id }
 * - event: event object
 * - onGameComplete: callback(result)
 * - onCancel: callback to go back
 * - onBack: callback to go back (alias)
 */
const VolleyballScoring = ({
  gameCommonId,
  gameSettings,
  participantAInfo,
  participantBInfo,
  // Full team rosters (unnarrowed by any per-match player subset) — used by
  // the mid-match player swap UI to offer bench players beyond who's
  // currently on court.
  participantATeamRoster = [],
  participantBTeamRoster = [],
  event,
  onGameComplete,
  onCancel,
  onBack,
  onBackToSettings,
  onFirstPointScored,
  onGamePointsStatus,
  isScorer = true,
  // Centralized pause state from GameComponent — when true, disable all scoring inputs.
  paused = false,
}) => {
  const { theme } = useTheme();

  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [currentPhase, setCurrentPhase] = useState('scoring'); // 'scoring' | 'completed'
  const [confirmModal, setConfirmModal] = useState({ isOpen: false });

  // Holds a summary of the just-completed set while waiting for the scorer
  // to start the next one — mirrors pickleball's between-game interstitial.
  const [completedSetSummary, setCompletedSetSummary] = useState(null);

  // "Enter Final Score" — lets the scorer skip rally-by-rally scoring and
  // complete the current set (and, if it's the deciding set, the match) by
  // typing in both teams' final scores directly.
  const [showFinalScoreModal, setShowFinalScoreModal] = useState(false);
  const [finalScoreInputs, setFinalScoreInputs] = useState({ a: '', b: '' });
  const [finalScoreSubmitting, setFinalScoreSubmitting] = useState(false);

  // Mid-set player swap — lets the scorer substitute a different roster
  // player into one side while the set is live.
  const [swapModal, setSwapModal] = useState(null); // { side: 'A' | 'B', oldPlayer: { id, name } }
  const [swapping, setSwapping] = useState(false);

  // Individual server tracking — simplified picker, no rotation-order
  // enforcement. `game.needsServerSelection` (server-computed) drives when
  // this UI shows; `serving` here just tracks the in-flight API call.
  const [servingPicking, setServingPicking] = useState(false);

  // Detailed scoring state — Kill/Block/Ace winners, Attack/Service/Ball-
  // Handling errors, with setter ("assisted by") credit on Kills.
  const [detailedScoring, setDetailedScoring] = useState(false);
  const [pendingWinningTeam, setPendingWinningTeam] = useState(null);
  const [scoredByPlayerId, setScoredByPlayerId] = useState(null);
  const [assistByPlayerId, setAssistByPlayerId] = useState(null);
  const [errorByPlayerId, setErrorByPlayerId] = useState(null);

  const resetDetailState = () => {
    setPendingWinningTeam(null);
    setScoredByPlayerId(null);
    setAssistByPlayerId(null);
    setErrorByPlayerId(null);
  };

  const SHOT_TYPES = [
    { key: 'kill', label: 'Kill', category: 'winner' },
    { key: 'block', label: 'Block', category: 'winner' },
    { key: 'ace', label: 'Ace', category: 'winner' },
    { key: 'attack_error', label: 'Attack Error', category: 'error' },
    { key: 'service_error', label: 'Service Error', category: 'error' },
    { key: 'ball_handling_error', label: 'Ball-Handling Error', category: 'error' },
    { key: 'other', label: 'Other', category: 'other' },
  ];

  // Load game on mount
  useEffect(() => {
    if (!gameCommonId) {
      setError('No game ID provided.');
      setLoading(false);
      return;
    }

    const load = async () => {
      try {
        setLoading(true);
        const loaded = await volleyballGameAPI.getById(gameCommonId);
        setGame(loaded);
        if (loaded.status === 'completed') {
          setCurrentPhase('completed');
        } else {
          setCurrentPhase('scoring');
        }
        const hasPoints = (loaded.rallies?.length || 0) > 0 || (loaded.matches?.length || 0) > 0;
        if (onGamePointsStatus) onGamePointsStatus(hasPoints);
      } catch (err) {
        console.error('[VolleyballScoring] Failed to load game:', err);
        setError('Failed to load game. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [gameCommonId]);

  // Determine if the current set is complete
  const isSetComplete = useCallback(() => {
    if (!game) return false;
    const isFinalSet = game.currentMatch === game.bestOf;
    const pointsToWin = isFinalSet
      ? (game.finalSetPoints || 15)
      : (game.maxPoints || 25);
    const { playerAScore, playerBScore, winBy2 } = game;
    if (winBy2) {
      return (
        (playerAScore >= pointsToWin && playerAScore - playerBScore >= 2) ||
        (playerBScore >= pointsToWin && playerBScore - playerAScore >= 2)
      );
    }
    return playerAScore >= pointsToWin || playerBScore >= pointsToWin;
  }, [game]);

  // Determine if the overall match is complete
  const isMatchComplete = useCallback(() => {
    if (!game) return false;
    const setsToWin = Math.ceil(game.bestOf / 2);
    return game.playerAMatchesWon >= setsToWin || game.playerBMatchesWon >= setsToWin;
  }, [game]);

  // Record a point for team A (winner='A') or team B (winner='B'), optionally
  // with detailed shot-type/scorer/setter/error attribution.
  const handlePointScored = useCallback(async (winner, detail = {}) => {
    if (!game || !gameCommonId || isSetComplete() || saving) return;

    const isFirstPoint = (game.rallies?.length || 0) === 0 && (game.matches?.length || 0) === 0;
    setSaving(true);
    setError(null);
    try {
      const result = await volleyballGameAPI.recordRally(gameCommonId, {
        pointWinner: winner,
        scoringPlayerId: detail.scoringPlayerId || null,
        shotType: detail.shotType || null,
        assistPlayerId: detail.assistPlayerId || null,
        errorPlayerId: detail.errorPlayerId || null,
      });
      setGame(result.game);
      resetDetailState();
      if (isFirstPoint && onFirstPointScored) onFirstPointScored();
    } catch (err) {
      console.error('[VolleyballScoring] Failed to record point:', err);
      setError('Failed to save point. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [game, gameCommonId, isSetComplete, saving]);

  // Team-score tap handler — in simple mode records the point immediately;
  // in detailed mode opens the shot-type/scorer/setter/error panel instead.
  const isTeamOneOnCourtPlayer = (team) => (team === 'A' ? game?.playerA : game?.playerB)?.players?.length === 1;

  const handleTeamWin = useCallback((winner) => {
    if (!game) return;
    if (!detailedScoring) {
      handlePointScored(winner);
      return;
    }
    setPendingWinningTeam(winner);
    setScoredByPlayerId(null);
    setAssistByPlayerId(null);
    setErrorByPlayerId(null);
    // Auto-resolve "who scored" when the team only has one player on court.
    if (isTeamOneOnCourtPlayer(winner)) {
      const only = (winner === 'A' ? game.playerA : game.playerB)?.players?.[0];
      if (only) setScoredByPlayerId(only.id);
    }
  }, [game, detailedScoring, handlePointScored]);

  // Tapping a winning-shot button IS the submitting action — no separate
  // "Confirm" hop. Scorer/setter are pre-selected beforehand (setter is
  // optional and only actually applies to Kills; harmlessly ignored for
  // Block/Ace). Ace always auto-resolves to whoever is currently serving.
  const handleWinningShot = useCallback((shotKey) => {
    if (pendingWinningTeam == null || !game) return;
    const scorerId = shotKey === 'ace' ? (game.serverPlayerId || null) : (scoredByPlayerId || null);
    handlePointScored(pendingWinningTeam, {
      scoringPlayerId: scorerId,
      shotType: shotKey,
      assistPlayerId: shotKey === 'kill' ? (assistByPlayerId || null) : null,
    });
  }, [pendingWinningTeam, scoredByPlayerId, assistByPlayerId, game, handlePointScored]);

  // Tapping an error-type button submits immediately. If the losing side has
  // exactly one on-court player, the error is unambiguous and auto-resolves
  // even without an explicit pre-selection.
  const handleErrorShot = useCallback((shotKey) => {
    if (pendingWinningTeam == null || !game) return;
    const losingPlayers = pendingWinningTeam === 'A' ? game.playerB?.players : game.playerA?.players;
    const resolvedErrorId = errorByPlayerId || (losingPlayers?.length === 1 ? losingPlayers[0].id : null);
    handlePointScored(pendingWinningTeam, {
      shotType: shotKey,
      errorPlayerId: resolvedErrorId,
    });
  }, [pendingWinningTeam, errorByPlayerId, game, handlePointScored]);

  // Set (or re-pick, after a side-out) the individual serving player.
  const handleConfirmServe = useCallback(async (servingTeam, servingPlayerId) => {
    if (!gameCommonId || servingPicking) return;
    setServingPicking(true);
    setError(null);
    try {
      const updated = await volleyballGameAPI.setInitialServe(gameCommonId, { servingTeam, servingPlayerId });
      setGame(updated);
    } catch (err) {
      console.error('[VolleyballScoring] Failed to set server:', err);
      setError('Failed to set server. Please try again.');
    } finally {
      setServingPicking(false);
    }
  }, [gameCommonId, servingPicking]);

  // Undo last point
  const handleUndo = useCallback(async () => {
    if (!game || !gameCommonId || (game.rallies?.length || 0) === 0 || saving) return;

    setSaving(true);
    setError(null);
    try {
      const updated = await volleyballGameAPI.undoLastRally(gameCommonId);
      setGame(updated);
    } catch (err) {
      console.error('[VolleyballScoring] Failed to undo:', err);
      setError('Failed to undo. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [game, gameCommonId, saving]);

  // Complete the current set
  const handleCompleteSet = useCallback(async () => {
    if (!game || !gameCommonId || !isSetComplete() || saving) return;

    // Capture the just-finished set's numbers before the API response
    // overwrites `game` with the next (0-0) set's state.
    const completedSetNumber = game.currentMatch;
    const completedPlayerAScore = game.playerAScore;
    const completedPlayerBScore = game.playerBScore;

    const winnerPlayer = game.playerAScore > game.playerBScore ? 'A' : 'B';
    setSaving(true);
    setError(null);
    try {
      const response = await volleyballGameAPI.completeSet(gameCommonId, { winnerPlayer });
      const updated = response.game || response;
      setGame(updated);
      if (updated.status === 'completed') {
        setCurrentPhase('completed');
      } else {
        setCompletedSetSummary({
          setNumber: completedSetNumber,
          playerAScore: completedPlayerAScore,
          playerBScore: completedPlayerBScore,
          playerAMatchesWon: updated.playerAMatchesWon,
          playerBMatchesWon: updated.playerBMatchesWon,
          nextSetNumber: updated.currentMatch
        });
      }
    } catch (err) {
      console.error('[VolleyballScoring] Failed to complete set:', err);
      setError('Failed to complete set. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [game, gameCommonId, isSetComplete, saving]);

  // A win-by-2 set ends the instant someone reaches the target with a 2+
  // point lead, so the winner's score can only exceed pointsToWin in a
  // genuine deuce situation (loser also within 1 point of the target) —
  // otherwise the winning score must be exactly pointsToWin. Mirrors the
  // backend's completeSetWithScore validation exactly.
  const isFinalScoreValid = () => {
    if (!game) return false;
    const a = parseInt(finalScoreInputs.a, 10);
    const b = parseInt(finalScoreInputs.b, 10);
    if (!Number.isInteger(a) || !Number.isInteger(b) || a < 0 || b < 0 || a === b) return false;
    const isFinalSet = game.currentMatch === game.bestOf;
    const pointsToWin = isFinalSet ? (game.finalSetPoints || 15) : (game.maxPoints || 25);
    const winner = Math.max(a, b);
    const loser = Math.min(a, b);
    const isDeuce = game.winBy2 && loser >= pointsToWin - 1;
    return isDeuce ? (winner - loser === 2) : (winner === pointsToWin);
  };

  // Skips rally-by-rally scoring entirely — completes the current set with
  // a directly-typed final score, then (only if this was the deciding set
  // of the best-of series) chains into the same completion flow
  // handleCompleteSet uses, via the fresh API response rather than the
  // (still-stale) `game` state.
  const handleSubmitFinalScore = async () => {
    if (!gameCommonId || finalScoreSubmitting || !isFinalScoreValid()) return;
    const scoreA = parseInt(finalScoreInputs.a, 10);
    const scoreB = parseInt(finalScoreInputs.b, 10);
    const completedSetNumber = game.currentMatch;

    setFinalScoreSubmitting(true);
    try {
      const response = await volleyballGameAPI.completeSetWithScore(gameCommonId, {
        participantAScore: scoreA,
        participantBScore: scoreB,
      });
      const updated = response.game || response;
      setGame(updated);
      setShowFinalScoreModal(false);
      setFinalScoreInputs({ a: '', b: '' });

      if (updated.status === 'completed') {
        setCurrentPhase('completed');
        if (onGameComplete) {
          const playerAWon = updated.playerAMatchesWon > updated.playerBMatchesWon;
          const winner = playerAWon ? updated.playerA : updated.playerB;
          await onGameComplete({
            winnerId: winner?.id,
            winnerType: winner?.type || 'team',
            scoreSummary: `${updated.playerAMatchesWon}-${updated.playerBMatchesWon}`,
            playerAScore: updated.playerAMatchesWon,
            playerBScore: updated.playerBMatchesWon,
            winner,
            gameId: updated.id,
          });
        }
      } else {
        setCompletedSetSummary({
          setNumber: completedSetNumber,
          playerAScore: scoreA,
          playerBScore: scoreB,
          playerAMatchesWon: updated.playerAMatchesWon,
          playerBMatchesWon: updated.playerBMatchesWon,
          nextSetNumber: updated.currentMatch
        });
      }
    } catch (err) {
      console.error('[VolleyballScoring] Failed to complete set with final score:', err);
      toast.error(err.message || 'Failed to complete set with that score.');
    } finally {
      setFinalScoreSubmitting(false);
    }
  };

  // Swap in a different roster player for one side mid-set.
  const handleConfirmSwap = async (newPlayer) => {
    if (!swapModal || !gameCommonId) return;
    setSwapping(true);
    try {
      const updated = await volleyballGameAPI.swapPlayer(gameCommonId, {
        side: swapModal.side,
        oldPlayerId: swapModal.oldPlayer.id,
        newPlayer,
      });
      setGame(updated);
      setSwapModal(null);
      toast.success(`Swapped in ${newPlayer.name}`);
    } catch (err) {
      console.error('[VolleyballScoring] Failed to swap player:', err);
      toast.error('Failed to swap player. Please try again.');
    } finally {
      setSwapping(false);
    }
  };

  // Complete the event
  const handleCompleteEvent = async () => {
    if (!game) return;

    const playerAWon = game.playerAMatchesWon > game.playerBMatchesWon;
    const winner = playerAWon ? game.playerA : game.playerB;

    const result = {
      winnerId: winner?.id,
      winnerType: winner?.type || 'team',
      scoreSummary: `${game.playerAMatchesWon}-${game.playerBMatchesWon}`,
      playerAScore: game.playerAMatchesWon,
      playerBScore: game.playerBMatchesWon,
      winner,
      gameId: game.id,
    };

    if (onGameComplete) {
      await onGameComplete(result);
    }
  };

  // Refresh game from backend
  const handleRefresh = async () => {
    try {
      const refreshed = await volleyballGameAPI.getById(gameCommonId);
      setGame(refreshed);
      if (refreshed.status === 'completed') setCurrentPhase('completed');
    } catch (err) {
      console.error('[VolleyballScoring] Failed to refresh:', err);
    }
  };


  // Generate scorecard text

  // Per-player stats across all sets — mirrors pickleball's calculatePlayerStats.
  // Declared here (not down by renderCompletedPhase, where it's used) because
  // it must run on every render including the loading/error early-returns
  // below — a hook placed after those guards is skipped on the first render
  // and picked up once `game` loads, changing the hook count between renders.
  const calculatePlayerStats = useCallback(() => {
    if (!game) return {};
    const allRallies = (game.matches || []).flatMap(m => m.rallies || []);
    if (allRallies.length === 0) return {};

    const stats = {};
    const allPlayers = [...(game.playerA?.players || []), ...(game.playerB?.players || []), ...participantATeamRoster, ...participantBTeamRoster];
    const seen = new Set();
    allPlayers.forEach(player => {
      if (seen.has(String(player.id))) return;
      seen.add(String(player.id));
      const teamA = (game.playerA?.players || []).some(p => String(p.id) === String(player.id))
        || participantATeamRoster.some(p => String(p.id) === String(player.id));
      stats[player.id] = {
        name: player.name,
        team: teamA ? 'A' : 'B',
        pointsWhileServing: 0,
        ralliesServed: 0,
        kills: 0,
        blocks: 0,
        aces: 0,
        errors: 0,
      };
    });

    const findStats = (id) => (id == null ? null : stats[id] || stats[String(id)] || null);

    allRallies.forEach(rally => {
      const serverStats = findStats(rally.serverPlayerId);
      if (serverStats) {
        serverStats.ralliesServed++;
        if (rally.pointWinner === serverStats.team) serverStats.pointsWhileServing++;
      }
      const scorerStats = findStats(rally.scoringPlayerId);
      if (scorerStats) {
        if (rally.shotType === 'kill') scorerStats.kills++;
        else if (rally.shotType === 'block') scorerStats.blocks++;
        else if (rally.shotType === 'ace') scorerStats.aces++;
      }
      const errorStats = findStats(rally.errorPlayerId);
      if (errorStats) errorStats.errors++;
    });

    Object.keys(stats).forEach(id => {
      const s = stats[id];
      s.servingEfficiency = s.ralliesServed > 0 ? Math.round((s.pointsWhileServing / s.ralliesServed) * 100) : 0;
    });

    return stats;
  }, [game, participantATeamRoster, participantBTeamRoster]);

  if (loading) {
    return (
      <div className={`min-h-screen ${theme.background} flex items-center justify-center`}>
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent" />
      </div>
    );
  }

  if (error && !game) {
    return (
      <div className={`p-6 ${theme.background}`}>
        <div className="flex items-center gap-2 text-red-600 bg-red-50 border border-red-200 rounded-lg p-4">
          <AlertCircle className="w-5 h-5 shrink-0" />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  // ─── Scoring Phase ────────────────────────────────────────────────────────
  const renderScoringPhase = () => {
    if (!game) return null;

    // Between-sets screen — shown after a set completes but before the next starts
    if (completedSetSummary) {
      const playerAWonSet = completedSetSummary.playerAScore > completedSetSummary.playerBScore;
      const winnerName = playerAWonSet ? game.playerA?.name : game.playerB?.name;
      return (
        <div className="space-y-4">
          <div className={`${theme.card} rounded-lg border ${theme.border} p-6 text-center space-y-3`}>
            <Trophy className="w-12 h-12 mx-auto text-yellow-500" />
            <div className={`text-lg font-bold ${theme.text}`}>
              Set {completedSetSummary.setNumber} Complete
            </div>
            <div className={`text-3xl font-bold ${theme.text}`}>
              {completedSetSummary.playerAScore} – {completedSetSummary.playerBScore}
            </div>
            <div className={`text-sm ${theme.textMuted}`}>
              <span className="font-semibold">{winnerName}</span> wins this set
            </div>
            <div className={`text-sm ${theme.textMuted}`}>
              Series: {game.playerA?.name} <span className="font-bold">{completedSetSummary.playerAMatchesWon}</span>
              {' – '}
              <span className="font-bold">{completedSetSummary.playerBMatchesWon}</span> {game.playerB?.name}
            </div>
          </div>

          {isScorer && (
            <button
              onClick={() => setCompletedSetSummary(null)}
              className={`w-full ${theme.accent} ${theme.accentHover} py-4 rounded-lg font-semibold text-lg flex items-center justify-center gap-2`}
            >
              <RefreshCw className="w-5 h-5" />
              Start Set {completedSetSummary.nextSetNumber}
            </button>
          )}
        </div>
      );
    }

    const setComplete = isSetComplete();
    const matchComplete = isMatchComplete();
    const isFinalSet = game.currentMatch === game.bestOf;
    const pointsToWin = isFinalSet
      ? (game.finalSetPoints || 15)
      : (game.maxPoints || 25);
    // Rallies scored in the CURRENT set specifically — game.rallies is
    // ambiguous between sets (it falls back to ALL completed sets'
    // flattened rallies whenever no set is in_progress yet), which would
    // wrongly read as non-zero right after a set completes and before the
    // next one's first point.
    const currentSetInMatches = (game.matches || []).find(m => m.matchNumber === game.currentMatch);
    const ralliesCount = currentSetInMatches ? (currentSetInMatches.rallies?.length || 0) : 0;
    const setsToWin = Math.ceil(game.bestOf / 2);
    // Server-authoritative serving state (individual server tracking) rather
    // than inferring team-only from the last rally's winner.
    const servingTeam = game.servingTeam || null;
    const serverPlayerId = game.serverPlayerId || null;
    // Individual server selection is only relevant/required when Detailed
    // Scoring is on — plain point-by-point scoring never needs it. The
    // team-level "who's serving" highlight above (servingTeam) still works
    // in simple mode; only the player-level picker gates on this.
    const needsServerSelection = detailedScoring && !!game.needsServerSelection;
    const serverName = serverPlayerId
      ? ((servingTeam === 'A' ? game.playerA?.players : game.playerB?.players) || []).find(p => String(p.id) === String(serverPlayerId))?.name
      : null;

    return (
      <div className="space-y-4">
        {/* Match info bar */}
        <div className={`${theme.card} rounded-lg border ${theme.border} p-4`}>
          <div className="text-center">
            <div className={`text-sm ${theme.textMuted} mb-1`}>
              Set {game.currentMatch} of {game.bestOf}
              {isFinalSet && <span className="ml-2 text-amber-600 font-medium">(Final Set — {pointsToWin} pts)</span>}
            </div>
            <div className={`text-lg font-semibold ${theme.text}`}>
              {game.playerA?.name} ({game.playerAMatchesWon})
              <span className={`mx-2 ${theme.textMuted}`}>vs</span>
              {game.playerB?.name} ({game.playerBMatchesWon})
            </div>
            {/* Set-by-set score breakdown, including the live in-progress set */}
            {(game.matches || []).length > 0 && (
              <div className={`text-xs ${theme.textMuted} mt-1 flex flex-wrap items-center justify-center gap-x-3 gap-y-0.5`}>
                {game.matches.map(set => (
                  <span key={set.matchNumber}>
                    Set {set.matchNumber}: <span className={`font-medium ${theme.text}`}>{set.playerAScore}-{set.playerBScore}</span>
                  </span>
                ))}
              </div>
            )}
            <div className={`text-xs ${theme.textMuted} mt-1`}>
              First to {setsToWin} sets wins the match
            </div>
          </div>
          {/* Live indicator — paused state is shown by the centralized PausedBanner */}
          {!paused && (
            <div className="flex justify-center mt-2">
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                LIVE
              </span>
            </div>
          )}
        </div>

        {/* Centered score display — "called out" serving-side-first, e.g.
            announcer convention: the serving team's score always comes first,
            independent of which team is A/B. The big clickable score boxes
            below stay fixed to their own team and are unaffected. */}
        <div className={`${theme.card} rounded-lg border ${theme.border} p-3 text-center`}>
          <div className={`text-xs ${theme.textMuted} mb-1`}>Callout Score (serving team first)</div>
          <div className={`text-3xl font-bold tracking-widest ${theme.text}`}>
            {servingTeam === 'B' ? game.playerBScore : game.playerAScore}
            {' – '}
            {servingTeam === 'B' ? game.playerAScore : game.playerBScore}
          </div>
          {servingTeam && (
            <div className={`text-xs ${theme.textMuted} mt-1`}>
              Serving: <span className="font-semibold">{serverName || (servingTeam === 'A' ? game.playerA?.name : game.playerB?.name)}</span>
              {serverName && <span> ({servingTeam === 'A' ? game.playerA?.name : game.playerB?.name})</span>}
            </div>
          )}
        </div>

        {/* Score Buttons */}
        <div className="grid grid-cols-2 gap-4">
          {/* Team A */}
          <div className={`${theme.card} rounded-lg border p-6 text-center transition-all
              ${servingTeam === 'A' ? 'ring-2 ring-green-500' : `border ${theme.border}`}`}
          >
            <button
              onClick={() => handleTeamWin('A')}
              disabled={setComplete || saving || !isScorer || paused || needsServerSelection || (detailedScoring && pendingWinningTeam != null)}
              className={`w-full ${!setComplete && !saving && isScorer && !paused && !needsServerSelection ? `active:scale-95 ${theme.cardHover}` : 'cursor-not-allowed'}`}
            >
              <div className={`text-sm ${theme.textMuted} mb-2 truncate`}>{game.playerA?.name}</div>
              <div className={`text-6xl font-bold ${theme.text} mb-2`}>{game.playerAScore}</div>
              <div className={`text-sm ${servingTeam === 'A' ? 'text-green-600 font-medium' : theme.textMuted}`}>
                {servingTeam === 'A' ? 'SERVING' : `Sets: ${game.playerAMatchesWon}`}
              </div>
            </button>
            {game.swappable && isScorer && !setComplete && participantATeamRoster.length > (game.playerA?.players?.length || 0) && (
              <div className="mt-3 flex flex-col items-center gap-1">
                {(game.playerA?.players || []).map(player => (
                  <button
                    key={player.id}
                    type="button"
                    onClick={() => setSwapModal({ side: 'A', oldPlayer: player })}
                    className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg border ${theme.border} ${theme.textMuted} hover:opacity-80`}
                  >
                    <RefreshCw className="w-3 h-3" /> Swap {player.name?.split(' ')[0] || player.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Team B */}
          <div className={`${theme.card} rounded-lg border p-6 text-center transition-all
              ${servingTeam === 'B' ? 'ring-2 ring-blue-500' : `border ${theme.border}`}`}
          >
            <button
              onClick={() => handleTeamWin('B')}
              disabled={setComplete || saving || !isScorer || paused || needsServerSelection || (detailedScoring && pendingWinningTeam != null)}
              className={`w-full ${!setComplete && !saving && isScorer && !paused && !needsServerSelection ? `active:scale-95 ${theme.cardHover}` : 'cursor-not-allowed'}`}
            >
              <div className={`text-sm ${theme.textMuted} mb-2 truncate`}>{game.playerB?.name}</div>
              <div className={`text-6xl font-bold ${theme.text} mb-2`}>{game.playerBScore}</div>
              <div className={`text-sm ${servingTeam === 'B' ? 'text-blue-600 font-medium' : theme.textMuted}`}>
                {servingTeam === 'B' ? 'SERVING' : `Sets: ${game.playerBMatchesWon}`}
              </div>
            </button>
            {game.swappable && isScorer && !setComplete && participantBTeamRoster.length > (game.playerB?.players?.length || 0) && (
              <div className="mt-3 flex flex-col items-center gap-1">
                {(game.playerB?.players || []).map(player => (
                  <button
                    key={player.id}
                    type="button"
                    onClick={() => setSwapModal({ side: 'B', oldPlayer: player })}
                    className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg border ${theme.border} ${theme.textMuted} hover:opacity-80`}
                  >
                    <RefreshCw className="w-3 h-3" /> Swap {player.name?.split(' ')[0] || player.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Server selection — initial pick (both team + player unknown) */}
        {isScorer && needsServerSelection && !servingTeam && !setComplete && (
          <div className={`${theme.card} rounded-lg border ${theme.border} p-4 space-y-4`}>
            <h3 className={`${theme.text} font-semibold text-center`}>Who is serving first?</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <div className="text-xs font-semibold text-center uppercase tracking-wide text-green-500 truncate">{game.playerA?.name}</div>
                {(game.playerA?.players || []).map(player => (
                  <button
                    key={player.id}
                    onClick={() => handleConfirmServe('A', player.id)}
                    disabled={servingPicking || paused}
                    className="w-full bg-green-600 hover:bg-green-700 active:bg-green-800 active:scale-95 text-white py-4 rounded-lg font-semibold transition-transform disabled:opacity-50"
                  >
                    {player.name?.split(' ')[0] || player.name}
                  </button>
                ))}
              </div>
              <div className="flex flex-col gap-2">
                <div className="text-xs font-semibold text-center uppercase tracking-wide text-blue-400 truncate">{game.playerB?.name}</div>
                {(game.playerB?.players || []).map(player => (
                  <button
                    key={player.id}
                    onClick={() => handleConfirmServe('B', player.id)}
                    disabled={servingPicking || paused}
                    className="w-full bg-blue-600 hover:bg-blue-700 active:bg-blue-800 active:scale-95 text-white py-4 rounded-lg font-semibold transition-transform disabled:opacity-50"
                  >
                    {player.name?.split(' ')[0] || player.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Server selection — mid-set re-pick after a side-out (team known, player unknown) */}
        {isScorer && needsServerSelection && servingTeam && !setComplete && (
          <div className={`${theme.card} rounded-lg border ${theme.border} p-4 space-y-3`}>
            <h3 className={`${theme.text} font-semibold text-center`}>
              Who serves for {servingTeam === 'A' ? game.playerA?.name : game.playerB?.name}?
            </h3>
            <div className="flex flex-col gap-2">
              {((servingTeam === 'A' ? game.playerA?.players : game.playerB?.players) || []).map(player => (
                <button
                  key={player.id}
                  onClick={() => handleConfirmServe(servingTeam, player.id)}
                  disabled={servingPicking || paused}
                  className={`w-full py-5 px-4 rounded-lg font-semibold transition-transform active:scale-95 text-white disabled:opacity-50 ${
                    servingTeam === 'A' ? 'bg-green-600 hover:bg-green-700 active:bg-green-800' : 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800'
                  }`}
                >
                  {player.name?.split(' ')[0] || player.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Viewer waiting for scorer to select server */}
        {!isScorer && needsServerSelection && !setComplete && (
          <div className={`${theme.card} rounded-lg border ${theme.border} p-6 text-center`}>
            <p className={theme.textMuted}>Waiting for scorer to set the server...</p>
          </div>
        )}

        {/* Saving indicator */}
        {saving && (
          <div className={`text-center text-sm ${theme.textMuted} flex items-center justify-center gap-2`}>
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent" />
            Saving...
          </div>
        )}

        {isScorer && !setComplete && !saving && !paused && !needsServerSelection && (
          <p className={`text-center text-sm ${theme.textMuted}`}>
            Tap a team's score to add a point
          </p>
        )}

        {/* Skip rally-by-rally scoring and enter a final score directly —
            only available before any point has been recorded in this set */}
        {isScorer && !setComplete && ralliesCount === 0 && !paused && (
          <button
            onClick={() => setShowFinalScoreModal(true)}
            className={`w-full text-sm py-2 rounded-lg border ${theme.border} ${theme.textMuted} hover:${theme.text}`}
          >
            Enter Final Score Instead
          </button>
        )}

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        )}

        {/* Detailed Scoring Toggle — scorer only, always available regardless
            of whether a server pick is currently pending */}
        {isScorer && !setComplete && (
          <label className={`flex items-center gap-2 text-sm ${theme.textMuted} cursor-pointer select-none`}>
            <input
              type="checkbox"
              checked={detailedScoring}
              onChange={e => { setDetailedScoring(e.target.checked); resetDetailState(); }}
              className="rounded"
            />
            <span>Enable Detailed Scoring</span>
          </label>
        )}

        {/* Detail scoring panel — visible as soon as a team has been tapped in detailed mode */}
        {detailedScoring && pendingWinningTeam != null && (() => {
          const SHOT_COLORS = {
            kill:                 { active: 'bg-red-600 text-white ring-2 ring-red-300',      idle: 'bg-red-100 text-red-800 hover:bg-red-200' },
            block:                { active: 'bg-purple-600 text-white ring-2 ring-purple-300', idle: 'bg-purple-100 text-purple-800 hover:bg-purple-200' },
            ace:                  { active: 'bg-emerald-600 text-white ring-2 ring-emerald-300', idle: 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200' },
            attack_error:         { active: 'bg-orange-600 text-white ring-2 ring-orange-300', idle: 'bg-orange-100 text-orange-800 hover:bg-orange-200' },
            service_error:        { active: 'bg-amber-600 text-white ring-2 ring-amber-300',   idle: 'bg-amber-100 text-amber-800 hover:bg-amber-200' },
            ball_handling_error:  { active: 'bg-rose-600 text-white ring-2 ring-rose-300',      idle: 'bg-rose-100 text-rose-800 hover:bg-rose-200' },
            other:                { active: 'bg-gray-600 text-white ring-2 ring-gray-300',     idle: 'bg-gray-200 text-gray-700 hover:bg-gray-300' },
          };
          const winningPlayers = pendingWinningTeam === 'A' ? game.playerA?.players : game.playerB?.players;
          const losingPlayers = pendingWinningTeam === 'A' ? game.playerB?.players : game.playerA?.players;
          const isDoublesOrMore = (winningPlayers?.length || 0) > 1;
          const losingIsDoublesOrMore = (losingPlayers?.length || 0) > 1;

          return (
            <div className={`${theme.card} rounded-lg border ${theme.border} p-4 space-y-4`}>
              <div className="flex items-center justify-between">
                <h3 className={`${theme.text} font-semibold`}>
                  {(() => {
                    const scorer = scoredByPlayerId ? winningPlayers?.find(p => String(p.id) === String(scoredByPlayerId)) : null;
                    return scorer?.name || (pendingWinningTeam === 'A' ? game.playerA?.name : game.playerB?.name);
                  })()} Won Point
                </h3>
                <button onClick={resetDetailState} className={`text-sm ${theme.textMuted} hover:${theme.text}`}>Cancel</button>
              </div>

              {/* Who scored? — pre-selected before tapping a winning shot below
                  (required for Kill/Block; Ace auto-resolves to the server) */}
              {isDoublesOrMore && (
                <div>
                  <div className={`text-sm font-medium ${theme.textMuted} mb-2`}>Who scored the point?</div>
                  <div className="grid grid-cols-2 gap-2">
                    {(winningPlayers || []).map(player => (
                      <button
                        key={player.id}
                        onClick={() => setScoredByPlayerId(prev => String(prev) === String(player.id) ? null : player.id)}
                        className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                          String(scoredByPlayerId) === String(player.id) ? 'bg-green-600 text-white' : `${theme.secondary} ${theme.secondaryHover} ${theme.text}`
                        }`}
                      >
                        {player.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Assisted by (setter) — optional, only meaningful for Kills;
                  pre-selected here since tapping "Kill" below submits immediately */}
              {isDoublesOrMore && scoredByPlayerId != null && (
                <div>
                  <div className={`text-sm font-medium ${theme.textMuted} mb-2`}>Assisted by (setter) — optional, for Kills</div>
                  <div className="grid grid-cols-2 gap-2">
                    {(winningPlayers || []).filter(p => String(p.id) !== String(scoredByPlayerId)).map(player => (
                      <button
                        key={player.id}
                        onClick={() => setAssistByPlayerId(prev => String(prev) === String(player.id) ? null : player.id)}
                        className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                          String(assistByPlayerId) === String(player.id) ? 'bg-indigo-600 text-white' : `${theme.secondary} ${theme.secondaryHover} ${theme.text}`
                        }`}
                      >
                        {player.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <div className={`text-xs font-semibold uppercase tracking-wide ${theme.textMuted} mb-1.5`}>Winning Shot — tap to record</div>
                <div className="grid grid-cols-3 gap-2">
                  {SHOT_TYPES.filter(s => s.category === 'winner').map(shot => {
                    const colors = SHOT_COLORS[shot.key];
                    const disabled = isDoublesOrMore && shot.key !== 'ace' && scoredByPlayerId == null;
                    return (
                      <button
                        key={shot.key}
                        disabled={disabled || saving}
                        onClick={() => handleWinningShot(shot.key)}
                        className={`py-2 px-2 rounded-lg text-sm font-semibold transition-all ${
                          disabled ? 'opacity-40 cursor-not-allowed bg-gray-100 text-gray-400' : colors.idle
                        }`}
                      >
                        {shot.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Who made the error? — optional, pre-selected before tapping
                  an error type below (only when the losing team has more than
                  one on-court player; otherwise auto-resolved on tap) */}
              {losingIsDoublesOrMore && (
                <div>
                  <div className={`text-sm font-medium ${theme.textMuted} mb-2`}>Who made the error? — optional</div>
                  <div className="grid grid-cols-2 gap-2">
                    {(losingPlayers || []).map(player => (
                      <button
                        key={player.id}
                        onClick={() => setErrorByPlayerId(prev => String(prev) === String(player.id) ? null : player.id)}
                        className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                          String(errorByPlayerId) === String(player.id) ? 'bg-rose-600 text-white' : `${theme.secondary} ${theme.secondaryHover} ${theme.text}`
                        }`}
                      >
                        {player.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <div className={`text-xs font-semibold uppercase tracking-wide ${theme.textMuted} mb-1.5`}>Opponent Error — tap to record</div>
                <div className="grid grid-cols-3 gap-2">
                  {SHOT_TYPES.filter(s => s.category === 'error' || s.category === 'other').map(shot => {
                    const colors = SHOT_COLORS[shot.key];
                    return (
                      <button
                        key={shot.key}
                        disabled={saving}
                        onClick={() => handleErrorShot(shot.key)}
                        className={`py-2 px-2 rounded-lg text-sm font-semibold transition-all ${colors.idle}`}
                      >
                        {shot.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })()}

        {/* Primary action */}
        {isScorer && !paused && !needsServerSelection && !(detailedScoring && pendingWinningTeam != null) && (setComplete ? (
          <button
            onClick={handleCompleteSet}
            disabled={saving}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4 rounded-lg font-semibold text-lg flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {saving ? (
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
            ) : (
              <>
                <Trophy className="w-5 h-5" />
                {matchComplete ? 'Complete Match' : `Complete Set ${game.currentMatch}`}
              </>
            )}
          </button>
        ) : ralliesCount > 0 ? (
          <button
            onClick={handleUndo}
            disabled={saving}
            className={`w-full py-3 rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50 border ${theme.border} ${theme.text}`}
          >
            <Undo2 className="w-4 h-4" />
            Undo Last Point
          </button>
        ) : null)}

        {/* Per-set point history */}
        {(game.matches || []).length > 0 && (() => {
          // Include full team rosters, not just currently-on-court players —
          // after a mid-set swap, an older rally's player ids may point at
          // someone no longer in game.playerA/B.players.
          const allPlayers = [...(game.playerA?.players || []), ...(game.playerB?.players || []), ...participantATeamRoster, ...participantBTeamRoster];
          const findPlayerName = (id) => id ? allPlayers.find(p => String(p.id) === String(id))?.name : null;

          return (
          <div className="space-y-2">
            {[...game.matches].reverse().map((match) => {
              const isInProgress = match.status === 'in_progress';
              const setRallies = isInProgress ? (game.rallies || []) : (match.rallies || []);
              const setWinnerIsA = match.winnerPlayer === 'A';

              return (
                <details key={match.matchNumber} className={`${theme.card} rounded-lg border ${theme.border}`}>
                  <summary className={`p-3 cursor-pointer flex items-center justify-between select-none`}>
                    {/* Set label + status */}
                    <span className={`font-medium ${theme.text}`}>
                      Set {match.matchNumber}
                      {isInProgress && (
                        <span className="ml-2 inline-flex items-center gap-1 text-xs font-medium text-red-600">
                          <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" />
                          LIVE
                        </span>
                      )}
                    </span>
                    {/* Score */}
                    <div className="flex items-center gap-3">
                      <span className={`font-bold text-lg ${!isInProgress && setWinnerIsA ? 'text-green-600' : theme.text}`}>
                        {match.playerAScore}
                      </span>
                      <span className={`text-sm ${theme.textMuted}`}>–</span>
                      <span className={`font-bold text-lg ${!isInProgress && !setWinnerIsA ? 'text-green-600' : theme.text}`}>
                        {match.playerBScore}
                      </span>
                      <span className={`text-xs ${theme.textMuted} ml-1`}>
                        ({setRallies.length} pts)
                      </span>
                    </div>
                  </summary>

                  {setRallies.length > 0 ? (
                    <div className="px-3 pb-3 max-h-52 overflow-y-auto">
                      {/* Rally rows — most recent first */}
                      {[...setRallies].reverse().map((rally, idx) => {
                        const scorerIsA = rally.pointWinner === 'A';
                        const newA = rally.playerAScoreBefore + (scorerIsA ? 1 : 0);
                        const newB = rally.playerBScoreBefore + (!scorerIsA ? 1 : 0);
                        const scorerName = findPlayerName(rally.scoringPlayerId);
                        const assistName = findPlayerName(rally.assistPlayerId);
                        const errorName = findPlayerName(rally.errorPlayerId);
                        const isErrorType = ['attack_error', 'service_error', 'ball_handling_error', 'other'].includes(rally.shotType);
                        const teamName = scorerIsA ? game.playerA?.name : game.playerB?.name;
                        return (
                          <div key={rally.id} className={`text-sm py-1.5 border-b ${theme.border} last:border-0`}>
                            <div>
                              <span className={theme.textMuted}>#{setRallies.length - idx}: </span>
                              <span className={scorerIsA ? 'text-green-600' : 'text-blue-600'}>
                                {scorerName || teamName} won
                              </span>
                              {rally.shotType && (
                                <span className={theme.textMuted}>
                                  {isErrorType
                                    ? ` — ${rally.shotType.replace(/_/g, ' ')}${errorName ? ` by ${errorName}` : ''}`
                                    : ` — ${rally.shotType}${assistName ? ` (set by ${assistName})` : ''}`}
                                </span>
                              )}
                              <span className={theme.textMuted}> — {newA}-{newB}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className={`px-3 pb-3 text-sm ${theme.textMuted}`}>No points recorded yet.</p>
                  )}
                </details>
              );
            })}
          </div>
          );
        })()}
      </div>
    );
  };

  // ─── Completed Phase ──────────────────────────────────────────────────────
  const renderCompletedPhase = () => {
    if (!game) return null;

    const playerAWon = game.playerAMatchesWon > game.playerBMatchesWon;
    const winnerName = game.winner?.name || (playerAWon ? game.playerA?.name : game.playerB?.name);
    const loserName = playerAWon ? game.playerB?.name : game.playerA?.name;
    const showCompleteEventButton = game.status === 'completed' && event?.status !== 'completed';
    const stats = calculatePlayerStats();

    return (
      <div className="space-y-6">
        {/* Winner Banner */}
        <div className={`${theme.card} rounded-lg border ${theme.border} p-6 text-center`}>
          <Trophy className="w-16 h-16 mx-auto text-yellow-500 mb-4" />
          <h2 className={`text-2xl font-bold ${theme.text} mb-2`}>{winnerName} Wins!</h2>
          <div className={`text-lg ${theme.textMuted} mb-2`}>
            defeated <span className={theme.text}>{loserName}</span>
          </div>
          <div className={`text-xl font-semibold ${theme.text}`}>
            {game.playerAMatchesWon} – {game.playerBMatchesWon}
          </div>
          <div className={`text-sm ${theme.textMuted} mt-1`}>
            Best of {game.bestOf} sets
          </div>
        </div>

        {/* Set-by-set results */}
        <div className={`${theme.card} rounded-lg border ${theme.border} p-4`}>
          <h3 className={`${theme.text} font-semibold mb-4`}>Match Summary</h3>

          {/* Header row */}
          <div className={`grid grid-cols-3 gap-2 mb-4 pb-3 border-b ${theme.border}`}>
            <div className={`text-center ${playerAWon ? 'font-semibold' : ''}`}>
              <div className={`${theme.text} ${playerAWon ? 'text-green-600' : ''}`}>
                {game.playerA?.name}
                {playerAWon && <Trophy className="w-4 h-4 inline ml-1 text-yellow-500" />}
              </div>
              <div className={`text-2xl font-bold ${playerAWon ? 'text-green-600' : theme.text}`}>
                {game.playerAMatchesWon}
              </div>
              <div className={`text-xs ${theme.textMuted}`}>sets won</div>
            </div>
            <div className={`text-center ${theme.textMuted} flex items-center justify-center text-xl`}>
              VS
            </div>
            <div className={`text-center ${!playerAWon ? 'font-semibold' : ''}`}>
              <div className={`${theme.text} ${!playerAWon ? 'text-green-600' : ''}`}>
                {game.playerB?.name}
                {!playerAWon && <Trophy className="w-4 h-4 inline ml-1 text-yellow-500" />}
              </div>
              <div className={`text-2xl font-bold ${!playerAWon ? 'text-green-600' : theme.text}`}>
                {game.playerBMatchesWon}
              </div>
              <div className={`text-xs ${theme.textMuted}`}>sets won</div>
            </div>
          </div>

          {/* Set scores — collapsible with per-set point history */}
          <h4 className={`${theme.textMuted} text-sm font-medium mb-3`}>Set Scores</h4>
          <div className="space-y-2">
            {[...(game.matches || [])].reverse().map((match) => {
              const setWinnerIsA = match.winnerPlayer === 'A';
              const setRallies = match.rallies || [];
              const allPlayers = [...(game.playerA?.players || []), ...(game.playerB?.players || []), ...participantATeamRoster, ...participantBTeamRoster];
              const findPlayerName = (id) => id ? allPlayers.find(p => String(p.id) === String(id))?.name : null;
              return (
                <details key={match.id} className={`rounded-lg border ${theme.border} ${theme.backgroundSecondary}`}>
                  <summary className={`p-3 cursor-pointer flex items-center justify-between select-none`}>
                    <span className={`font-medium text-sm ${theme.textMuted}`}>Set {match.matchNumber}</span>
                    <div className="flex items-center gap-3">
                      <span className={`font-bold ${setWinnerIsA ? 'text-green-600' : theme.text}`}>
                        {match.playerAScore}
                      </span>
                      <span className={`text-sm ${theme.textMuted}`}>–</span>
                      <span className={`font-bold ${!setWinnerIsA ? 'text-green-600' : theme.text}`}>
                        {match.playerBScore}
                      </span>
                      <span className={`text-xs ${theme.textMuted} ml-1`}>
                        ({setRallies.length} pts)
                      </span>
                    </div>
                  </summary>
                  {setRallies.length > 0 && (
                    <div className="px-3 pb-3 max-h-52 overflow-y-auto">
                      {[...setRallies].reverse().map((rally, idx) => {
                        const scorerIsA = rally.pointWinner === 'A';
                        const newA = rally.playerAScoreBefore + (scorerIsA ? 1 : 0);
                        const newB = rally.playerBScoreBefore + (!scorerIsA ? 1 : 0);
                        const scorerName = findPlayerName(rally.scoringPlayerId);
                        const assistName = findPlayerName(rally.assistPlayerId);
                        const errorName = findPlayerName(rally.errorPlayerId);
                        const isErrorType = ['attack_error', 'service_error', 'ball_handling_error', 'other'].includes(rally.shotType);
                        const teamName = scorerIsA ? game.playerA?.name : game.playerB?.name;
                        return (
                          <div key={rally.id} className={`text-xs py-1.5 border-b ${theme.border} last:border-0`}>
                            <span className={theme.textMuted}>#{setRallies.length - idx}: </span>
                            <span className={`font-medium ${scorerIsA ? 'text-green-600' : 'text-blue-600'}`}>{scorerName || teamName} won</span>
                            {rally.shotType && (
                              <span className={theme.textMuted}>
                                {isErrorType
                                  ? ` — ${rally.shotType.replace(/_/g, ' ')}${errorName ? ` by ${errorName}` : ''}`
                                  : ` — ${rally.shotType}${assistName ? ` (set by ${assistName})` : ''}`}
                              </span>
                            )}
                            <span className={`${theme.textMuted} font-medium`}> — {newA}-{newB}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </details>
              );
            })}
          </div>
        </div>

        {/* Player Statistics */}
        {Object.keys(stats).length > 0 && (
          <div className={`${theme.card} rounded-lg border ${theme.border} p-4`}>
            <h3 className={`${theme.text} font-semibold mb-4`}>Player Statistics</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className={theme.textMuted}>
                    <th className="text-left py-2">Player</th>
                    <th className="text-center py-2">Kills</th>
                    <th className="text-center py-2">Blocks</th>
                    <th className="text-center py-2">Aces</th>
                    <th className="text-center py-2">Errors</th>
                    <th className="text-center py-2">Serving Eff.</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.values(stats).map((stat) => (
                    <tr key={stat.name} className={`${theme.text} border-t ${theme.border}`}>
                      <td className="py-2">{stat.name}</td>
                      <td className="text-center py-2">{stat.kills}</td>
                      <td className="text-center py-2">{stat.blocks}</td>
                      <td className="text-center py-2">{stat.aces}</td>
                      <td className="text-center py-2">{stat.errors}</td>
                      <td className="text-center py-2">{stat.servingEfficiency}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="space-y-3">
          {isScorer && showCompleteEventButton && (
            <button
              onClick={handleCompleteEvent}
              className="w-full bg-green-600 hover:bg-green-700 text-white py-4 rounded-lg font-semibold text-lg flex items-center justify-center gap-2"
            >
              <Check className="w-5 h-5" />
              Complete Event & Continue
            </button>
          )}
        </div>
      </div>
    );
  };

  // Skip rally-by-rally scoring — type in both teams' final scores and
  // complete the set (and, if it's the deciding set of the series, the
  // event) in one action. Only reachable before any rally has been recorded
  // in the current set.
  const renderFinalScoreModal = () => {
    if (!showFinalScoreModal || !game) return null;

    const valid = isFinalScoreValid();
    const isFinalSet = game.currentMatch === game.bestOf;
    const pointsToWin = isFinalSet ? (game.finalSetPoints || 15) : (game.maxPoints || 25);

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => !finalScoreSubmitting && setShowFinalScoreModal(false)}>
        <div className={`${theme.card} rounded-lg border ${theme.border} p-6 w-full max-w-md`} onClick={e => e.stopPropagation()}>
          <h3 className={`${theme.text} text-lg font-semibold mb-1`}>Enter Final Score</h3>
          <p className={`text-sm ${theme.textMuted} mb-4`}>
            Skips point-by-point scoring for this set. First to {pointsToWin}{game.winBy2 ? ', win by 2' : ''}.
          </p>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className={`block text-xs font-semibold mb-1 uppercase tracking-wide text-green-500 truncate`}>
                {game.playerA?.name}
              </label>
              <input
                type="number"
                min="0"
                inputMode="numeric"
                value={finalScoreInputs.a}
                onChange={e => setFinalScoreInputs(prev => ({ ...prev, a: e.target.value }))}
                disabled={finalScoreSubmitting}
                className={`w-full px-3 py-2 border rounded-lg text-center text-lg font-semibold ${theme.input}`}
              />
            </div>
            <div>
              <label className={`block text-xs font-semibold mb-1 uppercase tracking-wide text-blue-400 truncate`}>
                {game.playerB?.name}
              </label>
              <input
                type="number"
                min="0"
                inputMode="numeric"
                value={finalScoreInputs.b}
                onChange={e => setFinalScoreInputs(prev => ({ ...prev, b: e.target.value }))}
                disabled={finalScoreSubmitting}
                className={`w-full px-3 py-2 border rounded-lg text-center text-lg font-semibold ${theme.input}`}
              />
            </div>
          </div>
          <button
            onClick={handleSubmitFinalScore}
            disabled={!valid || finalScoreSubmitting}
            className="w-full py-3 rounded-lg font-medium transition-colors bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
          >
            {finalScoreSubmitting ? 'Completing…' : 'Complete Set'}
          </button>
          <button
            onClick={() => setShowFinalScoreModal(false)}
            disabled={finalScoreSubmitting}
            className={`w-full mt-2 ${theme.secondary} ${theme.secondaryHover} ${theme.text} py-2 rounded-lg disabled:opacity-50`}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  };

  const renderSwapModal = () => {
    if (!swapModal || !game) return null;

    const roster = swapModal.side === 'A' ? participantATeamRoster : participantBTeamRoster;
    const currentPlayer = swapModal.oldPlayer;
    const currentlyPlayingIds = (swapModal.side === 'A' ? game.playerA?.players : game.playerB?.players)?.map(p => String(p.id)) || [];
    const candidates = roster.filter(p => !currentlyPlayingIds.includes(String(p.id)));

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => !swapping && setSwapModal(null)}>
        <div className={`${theme.card} rounded-lg border ${theme.border} p-6 w-full max-w-md`} onClick={e => e.stopPropagation()}>
          <h3 className={`${theme.text} text-lg font-semibold mb-1`}>Swap Player</h3>
          <p className={`text-sm ${theme.textMuted} mb-4`}>
            Currently playing: <span className="font-medium">{currentPlayer?.name || '—'}</span>. Pick a replacement from the same team's roster.
          </p>
          <div className="space-y-2">
            {candidates.length === 0 ? (
              <p className={`text-sm ${theme.textMuted} text-center py-4`}>No other players available on this roster.</p>
            ) : candidates.map(player => (
              <button
                key={player.id}
                onClick={() => handleConfirmSwap(player)}
                disabled={swapping}
                className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${theme.secondary} ${theme.secondaryHover} disabled:opacity-50`}
              >
                {player.name}
              </button>
            ))}
          </div>
          <button
            onClick={() => setSwapModal(null)}
            disabled={swapping}
            className={`w-full mt-4 ${theme.secondary} ${theme.secondaryHover} ${theme.text} py-2 rounded-lg disabled:opacity-50`}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className={`${theme.background} p-4`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className={`text-xl font-bold ${theme.text}`}>Volleyball</h1>
        <button onClick={handleRefresh} className={`${theme.textMuted} hover:text-blue-500`} title="Refresh from server">
          <RotateCcw className="w-5 h-5" />
        </button>
      </div>

      {currentPhase === 'scoring' && renderScoringPhase()}
      {currentPhase === 'completed' && renderCompletedPhase()}

      {/* Confirm Modal */}
      {confirmModal.isOpen && (
        <ConfirmModal
          isOpen={confirmModal.isOpen}
          title={confirmModal.title}
          message={confirmModal.message}
          confirmText={confirmModal.confirmText}
          onConfirm={confirmModal.onConfirm}
          onClose={confirmModal.onCancel}
        />
      )}

      {renderFinalScoreModal()}
      {renderSwapModal()}
    </div>
  );
};

export default VolleyballScoring;
